import { useEffect } from "react";
import SEO from "../components/SEO";
import {
  Target,
  Eye,
  Heart,
  Users,
  Award,
  Globe,
  BookOpen,
  Star,
  ArrowRight,
  Quote,
  Linkedin,
  Facebook,
  Github,
  Mail,
} from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const AboutPage = () => {
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".page-header",
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.5, ease: "expo.out" },
      );

      gsap.fromTo(
        ".value-card",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.08,
          ease: "expo.out",
          scrollTrigger: {
            trigger: ".values-grid",
            start: "top 80%",
          },
        },
      );

      gsap.fromTo(
        ".team-card",
        { opacity: 0, scale: 0.95 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.4,
          stagger: 0.08,
          ease: "expo.out",
          scrollTrigger: {
            trigger: ".team-grid",
            start: "top 80%",
          },
        },
      );

      gsap.fromTo(
        ".timeline-item",
        { opacity: 0, x: -20 },
        {
          opacity: 1,
          x: 0,
          duration: 0.4,
          stagger: 0.12,
          ease: "expo.out",
          scrollTrigger: {
            trigger: ".timeline",
            start: "top 80%",
          },
        },
      );
    });

    return () => ctx.revert();
  }, []);

  const values = [
    {
      icon: Target,
      title: "Our Mission",
      description:
        "To democratize education by making high-quality learning accessible to everyone, everywhere. We believe that knowledge should have no boundaries.",
      color: "bg-blue-500",
    },
    {
      icon: Eye,
      title: "Our Vision",
      description:
        "To become the world's leading platform for lifelong learning, empowering millions to achieve their personal and professional goals.",
      color: "bg-purple-500",
    },
    {
      icon: Heart,
      title: "Our Values",
      description:
        "We put learners first, embrace innovation, foster inclusivity, and maintain the highest standards of educational excellence.",
      color: "bg-red-500",
    },
  ];

  const stats = [
    { value: "50+", label: "Courses", icon: BookOpen },
    { value: "30k+", label: "Students", icon: Users },
    { value: "100+", label: "Instructors", icon: Award },
    { value: "25+", label: "Countries", icon: Globe },
  ];

  const team = [
    {
      name: "Bongnteh Romarick Ndzelen",
      role: "Founder & CEO",
      image: "/romarick.jpeg", // Added image path
      bio: "Passionate about transforming education through technology. Building LearnHub to make quality learning accessible to everyone, everywhere.",
      social: {
        linkedin: "bongnteh-romarick-ndzelen-b2946023b",
        facebook: "Romarick10",
        github: "Bongnteh-Romarick-ndzelen",
        email: "ndzelenromarick@gmail.com",
      },
    },
  ];

  const timeline = [
    {
      year: "2020",
      title: "The Beginning",
      description:
        "LearnHub was founded with a vision to transform online education.",
    },
    {
      year: "2021",
      title: "First 10,000 Students",
      description:
        "Reached our first major milestone of 10,000 active learners.",
    },
    {
      year: "2022",
      title: "Global Expansion",
      description: "Expanded to 25+ countries and launched mentorship program.",
    },
    {
      year: "2023",
      title: "Industry Recognition",
      description: 'Named "Best Online Learning Platform" by EdTech Awards.',
    },
    {
      year: "2024",
      title: "The Future",
      description: "Continuing to innovate and empower learners worldwide.",
    },
  ];

  return (
    <>
      <SEO
        title="About LearnHub Institute - Our Mission & Story"
        description="LearnHub Institute is democratizing education with expert-led online courses and mentorship. Meet our founder and discover how we're transforming learning worldwide."
        keywords="about learnhub, online education, edtech, learning platform, mentorship, our mission"
      />
      <main className="relative min-h-screen pt-20 pb-16 bg-white">
      {/* Hero Section */}
      <div className="mb-12 section-container">
        <div className="max-w-4xl mx-auto text-center page-header">
          <span className="inline-block px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full mb-4">
            About Us
          </span>
          <h1 className="mb-4 text-2xl font-bold text-black lg:text-3xl xl:text-4xl">
            Empowering Learners <span className="text-gradient">Worldwide</span>
          </h1>
          <p className="max-w-3xl mx-auto text-sm leading-relaxed lg:text-base text-gray-medium">
            We're on a mission to make quality education accessible to everyone.
            Through innovative technology and expert instruction, we're
            transforming how people learn and grow.
          </p>
        </div>
      </div>

      {/* Stats Section */}
      <div className="mb-16 section-container">
        <div className="p-6 bg-gradient-to-br from-orange-50 to-white rounded-2xl lg:p-8">
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 bg-primary/10 rounded-xl">
                  <stat.icon className="w-5 h-5 text-primary" />
                </div>
                <p className="text-2xl font-bold text-black mb-0.5">
                  {stat.value}
                </p>
                <p className="text-xs text-gray-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mission, Vision, Values */}
      <div className="mb-16 section-container">
        <div className="grid gap-5 values-grid md:grid-cols-3">
          {values.map((value, index) => (
            <div
              key={index}
              className="p-5 transition-all duration-300 bg-white value-card rounded-xl shadow-card hover:shadow-card-hover hover:-translate-y-1"
            >
              <div
                className={`w-12 h-12 ${value.color} rounded-xl flex items-center justify-center mb-4`}
              >
                <value.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="mb-2 text-lg font-bold text-black">
                {value.title}
              </h3>
              <p className="text-sm leading-relaxed text-gray-medium">
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Story Section */}
      <div className="mb-16 section-container">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="inline-block px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full mb-4">
              Our Story
            </span>
            <h2 className="mb-4 text-xl font-bold text-black lg:text-2xl">
              From a Small Idea to a Global Movement
            </h2>
            <div className="space-y-3 text-sm leading-relaxed text-gray-medium">
              <p>
                LearnHub started in 2020 when our founder, Bongnteh Romarick
                Ndzelen, realized that traditional education wasn't keeping pace
                with the rapidly changing demands of the modern workplace. He
                envisioned a platform where anyone, anywhere could learn from
                industry experts.
              </p>
              <p>
                What began as a small collection of courses has grown into a
                global community of over 30,000 learners from 25+ countries. Our
                students have gone on to launch successful careers, start
                businesses, and make meaningful impacts in their fields.
              </p>
              <p>
                Today, LearnHub continues to innovate, offering not just courses
                but also personalized mentorship, career guidance, and a
                supportive community that helps learners achieve their goals.
              </p>
            </div>
          </div>
          <div className="relative">
            <img
              src="/hero-image.jpg"
              alt="Our Story"
              className="w-full shadow-lg rounded-xl"
            />
            <div className="absolute p-3 bg-white rounded-lg -bottom-4 -left-4 shadow-card">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary">
                  <Star className="w-5 h-5 text-white fill-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-black">4.9</p>
                  <p className="text-xs text-gray-medium">Student Rating</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="mb-16 section-container">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-black lg:text-2xl">
            Our Journey
          </h2>
          <p className="text-sm text-gray-medium">
            Milestones that mark our growth
          </p>
        </div>
        <div className="max-w-3xl mx-auto timeline">
          {timeline.map((item, index) => (
            <div
              key={index}
              className="flex gap-4 mb-6 timeline-item last:mb-0"
            >
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center w-10 h-10 text-sm font-bold text-white rounded-full bg-primary">
                  {item.year.slice(-2)}
                </div>
                {index < timeline.length - 1 && (
                  <div className="w-0.5 h-full bg-primary/20 mt-2" />
                )}
              </div>
              <div className="pb-6">
                <span className="text-xs font-semibold text-primary">
                  {item.year}
                </span>
                <h3 className="mt-1 mb-1 text-base font-bold text-black">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-medium">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Team Section - Single Founder */}
      <div className="mb-16 section-container">
        <div className="mb-8 text-center">
          <span className="inline-block px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full mb-3">
            Our Founder
          </span>
          <h2 className="mb-2 text-xl font-bold text-black lg:text-2xl">
            Meet the Visionary Behind LearnHub
          </h2>
          <p className="max-w-2xl mx-auto text-sm text-gray-medium">
            A passionate entrepreneur dedicated to transforming education
          </p>
        </div>
        <div className="max-w-2xl mx-auto team-grid">
          {team.map((member, index) => (
            <div
              key={index}
              className="overflow-hidden transition-all duration-300 bg-white team-card rounded-xl shadow-card hover:shadow-card-hover hover:-translate-y-1 group"
            >
              <div className="flex flex-col md:flex-row">
                <div className="relative h-64 overflow-hidden md:h-auto md:w-1/2 bg-gradient-to-br from-primary/10 to-orange-500/10">
                  <img
                    src="/romarick.jpeg"
                    alt={member.name}
                    className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      // Fallback to initial avatar if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      const parent = target.parentElement;
                      if (parent) {
                        const fallback = document.createElement("div");
                        fallback.className =
                          "flex items-center justify-center w-full h-full bg-gradient-to-br from-primary/20 to-orange-500/20";
                        fallback.innerHTML = `
                  <div class="w-32 h-32 rounded-full bg-gradient-to-br from-primary to-orange-500 flex items-center justify-center">
                    <span class="text-4xl font-bold text-white">${member.name.charAt(0)}</span>
                  </div>
                `;
                        parent.appendChild(fallback);
                      }
                    }}
                  />
                </div>
                <div className="p-6 md:w-1/2">
                  <h3 className="mb-1 text-lg font-bold text-black">
                    {member.name}
                  </h3>
                  <p className="mb-3 text-xs font-medium text-primary">
                    {member.role}
                  </p>
                  <p className="mb-4 text-sm leading-relaxed text-gray-medium">
                    {member.bio}
                  </p>
                  <div className="flex gap-3">
                    <a
                      href={`https://linkedin.com/in/${member.social.linkedin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 transition-colors bg-gray-100 rounded-lg hover:bg-primary/10 group"
                    >
                      <Linkedin className="w-4 h-4 text-gray-600 group-hover:text-primary" />
                    </a>
                    <a
                      href={`https://facebook.com/${member.social.facebook}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 transition-colors bg-gray-100 rounded-lg hover:bg-primary/10 group"
                    >
                      <Facebook className="w-4 h-4 text-gray-600 group-hover:text-primary" />
                    </a>
                    <a
                      href={`https://github.com/${member.social.github}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 transition-colors bg-gray-100 rounded-lg hover:bg-primary/10 group"
                    >
                      <Github className="w-4 h-4 text-gray-600 group-hover:text-primary" />
                    </a>
                    <a
                      href={`mailto:${member.social.email}`}
                      className="p-2 transition-colors bg-gray-100 rounded-lg hover:bg-primary/10 group"
                    >
                      <Mail className="w-4 h-4 text-gray-600 group-hover:text-primary" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonial */}
      <div className="mb-16 section-container">
        <div className="relative p-6 overflow-hidden bg-black rounded-xl lg:p-8">
          <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-primary/20 blur-3xl" />
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <Quote className="w-10 h-10 mx-auto mb-4 text-primary" />
            <blockquote className="mb-5 text-lg font-medium text-white lg:text-xl">
              "Education is not the filling of a pail, but the lighting of a
              fire. We're here to ignite that spark in every learner."
            </blockquote>
            <div className="flex items-center justify-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-primary to-orange-500">
                <span className="text-sm font-bold text-white">BN</span>
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-white">
                  Bongnteh Romarick Ndzelen
                </p>
                <p className="text-xs text-white/70">Founder & CEO, LearnHub</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="section-container">
        <div className="p-6 text-center bg-gradient-to-r from-primary to-orange-400 rounded-xl lg:p-8">
          <h2 className="mb-2 text-xl font-bold text-white lg:text-2xl">
            Join Our Growing Community
          </h2>
          <p className="max-w-2xl mx-auto mb-5 text-sm text-white/80">
            Be part of a global movement that's transforming education and
            empowering learners worldwide.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="/courses"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-primary text-sm font-medium rounded-lg hover:bg-white/90 transition-colors group"
            >
              Explore Courses
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <a
              href="/mentorship"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/20 text-white text-sm font-medium rounded-lg hover:bg-white/30 transition-colors"
            >
              Find a Mentor
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .text-gradient {
          background: linear-gradient(135deg, #E85D04 0%, #F48C06 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>
    </main>
    </>
  );
};

export default AboutPage;